import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { createAdminQueryClient } from './lib/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Categories from './pages/Categories';
import Brands from './pages/Brands';
import Campaigns from './pages/Campaigns';
import BoutiquePromotions from './pages/BoutiquePromotions';
import AdminMessaging from './pages/AdminMessaging';
import Products from './pages/Products';
import AddProduct from './pages/AddProduct';
import Orders from './pages/Orders';
import Payments from './pages/Payments';
import Bidding from './pages/Bidding';
import BidDetails from './pages/BidDetails';
import ProductBids from './pages/ProductBids';
import ShippingZones from './pages/ShippingZones';
import Attributes from './pages/Attributes';
import AttributeValues from './pages/AttributeValues';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AccountDeletion from './pages/AccountDeletion';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import ProtectedRoute from './components/ProtectedRoute';
import './i18n/config'; // Initialize i18n

const queryClient = createAdminQueryClient();

const LegacyOfferRedirect = () => {
  const { bidId } = useParams<{ bidId: string }>();
  return <Navigate to={bidId ? `/offers/${bidId}` : '/offers'} replace />;
};

const LegacyProductOffersRedirect = () => {
  const { productId } = useParams<{ productId: string }>();
  return <Navigate to={productId ? `/offers/product/${productId}` : '/offers'} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/account-deletion" element={<AccountDeletion />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute>
                <Layout>
                  <Users />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/boutique-promotions" element={
              <ProtectedRoute>
                <Layout>
                  <BoutiquePromotions />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/messaging" element={
              <ProtectedRoute>
                <Layout>
                  <AdminMessaging />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/products" element={
              <ProtectedRoute>
                <Layout>
                  <Products />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/products/add" element={
              <ProtectedRoute>
                <Layout>
                  <AddProduct />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <Layout>
                  <Orders />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/payments" element={
              <ProtectedRoute>
                <Layout>
                  <Payments />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/categories" element={
              <ProtectedRoute>
                <Layout>
                  <Categories />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/brands" element={
              <ProtectedRoute>
                <Layout>
                  <Brands />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/campaigns" element={
              <ProtectedRoute>
                <Layout>
                  <Campaigns />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/offers" element={
              <ProtectedRoute>
                <Layout>
                  <Bidding />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/offers/product/:productId" element={
              <ProtectedRoute>
                <Layout>
                  <ProductBids />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/offers/:offerId" element={
              <ProtectedRoute>
                <Layout>
                  <BidDetails />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bidding" element={<Navigate to="/offers" replace />} />
            <Route path="/bidding/product/:productId" element={<LegacyProductOffersRedirect />} />
            <Route path="/bidding/:bidId" element={<LegacyOfferRedirect />} />
            <Route path="/shipping-zones" element={
              <ProtectedRoute>
                <Layout>
                  <ShippingZones />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/attributes" element={
              <ProtectedRoute>
                <Layout>
                  <Attributes />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/attributes/:attributeId/values" element={
              <ProtectedRoute>
                <Layout>
                  <AttributeValues />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;